import { useGetTracking } from '@cobliteam/hooks/src/useEntity/useTracking';
import Environment from '@cobliteam/react-environments';
import { useFormContext } from '@cobliteam/ui/form';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import { APIProvider, Map as GoogleMap } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import type { FormSchemaType } from '../../mediaRequestSchema';
import MapEventMarkers from './MapEventMarkers';
import MapTrackingMarkers from './MapTrackingMarkers';
import MapTrackingPaths from './MapTrackingPaths';
import MapZoomControl from './MapZoomControl';

const env = Environment.getInstance().getEnvironment();

interface PathPoints {
  start: google.maps.LatLng;
  end: google.maps.LatLng;
}

/**
 * Renders a Google Map with vehicle tracking paths and event markers
 * Used in the media request workflow to visualize vehicle journeys and events
 */
const MediaRequestMap = () => {
  // Get form values for vehicle and date
  const { getValues } = useFormContext<FormSchemaType>();
  const { vehicle, date } = getValues();

  // State for tracking path endpoints
  const [baseStartEnd, setBaseStartEnd] = useState<PathPoints | null>(null);
  const [highlightStartEnd, setHighlightStartEnd] = useState<PathPoints | null>(null);

  // Fetch tracking data for the selected vehicle and date
  const { data: tracking } = useGetTracking(vehicle?.id, startOfDay(date).getTime(), endOfDay(date).getTime(), true, {
    enabled: !!vehicle?.id && !!date,
  });

  // Default map center (São Paulo, Brazil coordinates)
  const defaultCenter = { lat: -23.5505, lng: -46.6333 };

  return (
    <APIProvider apiKey={env.googleMapsKey || ''}>
      <GoogleMap mapId='media-request-map' defaultCenter={defaultCenter} defaultZoom={12} disableDefaultUI className='relative h-64 w-full'>
        {tracking && (
          <>
            {/* Render tracking path polylines */}
            <MapTrackingPaths trackingPath={tracking.pathGeojson} onBasePathChange={setBaseStartEnd} onHighlightPathChange={setHighlightStartEnd} />

            {/* Render start/end markers for paths */}
            <MapTrackingMarkers basePolylineStartEnd={baseStartEnd} highlightPolylineStartEnd={highlightStartEnd} />

            {/* Render event markers (incidents, etc.) */}
            <MapEventMarkers />

            {/* Render zoom controls */}
            <MapZoomControl />
          </>
        )}
      </GoogleMap>
    </APIProvider>
  );
};

export default MediaRequestMap;
